import React from 'react';
import { Asterisk, Check, Circle, Diamond, Minus, Plus, X } from 'lucide-react';
import { BaseTool, IToolComponentProps, IToolContext, IToolManifest } from '../../core/tools/interfaces/ITool';
import { CompositeCommand, ICommand } from '../../core/commands/ICommand';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { UpdateFlowchartNodeCommand } from '../../domains/process/commands/UpdateFlowchartNodeCommand';
import { useDiagramStore } from '../../stores/diagramStore';
import { useTranslation } from '../../hooks/useTranslation';

type BpmnGatewayType =
  | 'none'
  | 'exclusive'
  | 'event-based'
  | 'parallel'
  | 'parallel-event'
  | 'inclusive'
  | 'exclusive-event'
  | 'complex';

const ParallelEventIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="8" fill="#FFFFFF" stroke="#000000" />
    <path d="M10.5 6H13.5V10.5H18V13.5H13.5V18H10.5V13.5H6V10.5H10.5V6Z" fill="#FFFFFF" stroke="#000000" />
  </svg>
);

const EventGatewayIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <circle cx="12" cy="12" r="8" />
    <circle cx="12" cy="12" r="5.75" />
    <path d="M12 8.8L15.2 10.9L14 14.5H10L8.8 10.9L12 8.8Z" />
  </svg>
);

const ExclusiveEventIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <circle cx="12" cy="12" r="8" />
    <path d="M12 7L16.8 10.4L15.1 16H8.9L7.2 10.4L12 7Z" />
  </svg>
);

const GATEWAY_OPTIONS: Array<{
  value: BpmnGatewayType;
  Icon: React.ComponentType<{ className?: string }>;
}> = [
  { value: 'none', Icon: Minus },
  { value: 'exclusive', Icon: X },
  { value: 'event-based', Icon: EventGatewayIcon },
  { value: 'parallel', Icon: Plus },
  { value: 'parallel-event', Icon: ParallelEventIcon },
  { value: 'inclusive', Icon: Circle },
  { value: 'exclusive-event', Icon: ExclusiveEventIcon },
  { value: 'complex', Icon: Asterisk },
];

const normalizeVariant = (node: any): string => {
  const attrs = (node?.attrs as Record<string, unknown>) || {};
  const raw = attrs.variant || attrs.shapeType || node?.name || node?.type || '';
  return String(raw).toLowerCase().replace(/\s+/g, '-');
};

const isBpmnGatewayNode = (node: any): boolean => {
  const variant = normalizeVariant(node);
  return variant === 'bpmn-gateway' || variant.includes('gateway');
};

const BpmnGatewayTypeToolComponent: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const { t } = useTranslation('diagram');
  const [open, setOpen] = React.useState(false);

  const selectedNodes = React.useMemo(() => {
    if (typeof context.getSelectedShapes !== 'function') return [] as any[];
    return context
      .getSelectedShapes()
      .map((shape: any) => shape?.data)
      .filter(Boolean) as any[];
  }, [context, context.selection]);

  const gatewayNodes = React.useMemo(
    () => selectedNodes.filter((n) => isBpmnGatewayNode(n)),
    [selectedNodes]
  );

  const currentType = React.useMemo(() => {
    if (gatewayNodes.length === 0) return 'none' as BpmnGatewayType;
    const values = gatewayNodes.map((node) => {
      const attrs = (node.attrs as Record<string, unknown>) || {};
      return String(attrs.gatewayType ?? node.gatewayType ?? 'none').toLowerCase() as BpmnGatewayType;
    });
    const first = values[0] as BpmnGatewayType;
    return values.every((v) => v === first) ? first : first;
  }, [gatewayNodes]);

  const activeOption = GATEWAY_OPTIONS.find((opt) => opt.value === currentType) ?? GATEWAY_OPTIONS[0];
  const TriggerIcon = activeOption?.Icon ?? Diamond;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="tid.tools.bpmnGateway.trigger"
          className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded transition-colors"
          aria-label={t('tools.bpmnGateway.type', { defaultValue: 'Gateway type' })}
        >
          <TriggerIcon className="h-5 w-5 text-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-2" align="start">
        <div className="space-y-1">
          {GATEWAY_OPTIONS.map((option) => {
            const Icon = option.Icon;
            const isActive = option.value === currentType;
            return (
              <button
                key={option.value}
                type="button"
                data-testid={`tid.tools.bpmnGateway.option.${option.value}`}
                className="w-full flex items-center gap-3 px-3 py-2 rounded hover:bg-muted transition-colors text-start"
                onClick={() => {
                  onExecute({ gatewayType: option.value });
                  setOpen(false);
                }}
              >
                <Icon className="h-5 w-5 text-foreground" />
                <span className="text-[14px] text-foreground flex-1 min-w-0 text-start">
                  {t(`tools.bpmnGateway.types.${option.value}`)}
                </span>
                {isActive && <Check className="ml-auto h-4 w-4 text-foreground" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export class BpmnGatewayTypeTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'bpmn-gateway-type',
    name: 'tools.bpmnGateway.type',
    icon: 'diamond',
    category: 'style',
    description: 'Change BPMN gateway type from toolbar',
    version: '1.0.0',
    applicability: {
      diagramTypes: ['flowchart'],
      contextTypes: ['genericShape'],
    },
    requiresSelection: true,
    minSelection: 1,
    ui: {
      toolbar: true,
      contextMenu: false,
      propertyPanel: false,
      sidebar: false,
      floatingPanel: true,
      statusBar: false,
    },
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: true,
      supportsBatchOperations: true,
    },
  };

  canExecute(context: IToolContext): boolean {
    if (!super.canExecute(context) || context.selection.size === 0) return false;
    if (context.diagramType !== 'flowchart') return false;
    const selectedIds = Array.from(context.selection);
    const nodes = useDiagramStore.getState().nodes.filter((n) => selectedIds.includes(n.id));
    return nodes.length > 0 && nodes.every((node) => isBpmnGatewayNode(node));
  }

  execute(params?: { gatewayType?: BpmnGatewayType }): ICommand | null {
    if (!this.context || this.context.selection.size === 0 || !params?.gatewayType) return null;
    const selectedIds = Array.from(this.context.selection);
    const nodesById = new Map(useDiagramStore.getState().nodes.map((n) => [n.id, n]));
    const commands = selectedIds
      .map((nodeId) => {
        const node = nodesById.get(nodeId) as any;
        if (!node || !isBpmnGatewayNode(node)) return null;
        const attrs = (node.attrs as Record<string, unknown>) || {};
        return new UpdateFlowchartNodeCommand({
          nodeId,
          changes: {
            gatewayType: params.gatewayType,
            attrs: { ...attrs, gatewayType: params.gatewayType },
          } as any,
        });
      })
      .filter(Boolean) as ICommand[];

    if (commands.length === 0) return null;
    if (commands.length === 1) return commands[0];
    return new CompositeCommand('Update BPMN Gateway Type', commands);
  }

  getToolbarComponent() {
    return BpmnGatewayTypeToolComponent;
  }

  getFloatingPanelComponent() {
    return BpmnGatewayTypeToolComponent;
  }
}
